import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useSetPasswordMutation } from '@/store/api/Auth/Auth.query'
import { trackVslSetPasswordViewed } from '@/utils/analytics/analytics'
import type { createPasswordType } from '@/utils/schemas'
import { createPasswordSchema } from '@/utils/schemas'

export const SetPassword = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  useEffect(() => {
    trackVslSetPasswordViewed()
  }, [])

  const form = useForm<createPasswordType>({
    resolver: zodResolver(createPasswordSchema),
    defaultValues: {
      createPassword: '',
      confirmPassword: '',
    },
  })

  const {
    handleSubmit,
    control,
    formState: { errors = {}, dirtyFields },
    watch,
  } = form

  const passNotMatch =
    Object.keys(dirtyFields).includes('confirmPassword') &&
    watch().confirmPassword !== watch().createPassword

  const [setPassword, { isLoading }] = useSetPasswordMutation()

  const handleSetPassword = async (password: string) => {
    try {
      await setPassword({ password }).unwrap()
      toast.success(t('auth.setPassword.success'))
      navigate(AppRoutes.welcomeToSelfCheck)
    } catch (e) {
      catchWithSentry(e)
      toast.error(t('auth.setPassword.error'))
    }
  }

  const onSubmit = (data: createPasswordType) => {
    if (passNotMatch) {
      return
    }
    handleSetPassword(data.createPassword)
  }

  return (
    <div className="w-full flex flex-col items-center justify-center gap-8 min-h-screen">
      <div className="flex flex-col gap-8 max-w-[400px] w-full bg-baseColorHighlight p-8 rounded-3xl">
        <div className="flex flex-col gap-2 text-center">
          <p className="text-[32px] font-medium text-neutralColor">
            {t('auth.setPassword.title')}
          </p>
          <p className="text-base text-neutralColor60">
            {t('auth.setPassword.subtitle')}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6 w-full"
          >
            <FormField
              control={control}
              name="createPassword"
              render={({ field }) => (
                <FormItem className="w-full bg-white rounded-md">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input
                      {...field}
                      placeholder={t('auth.setPassword.createPassword')}
                      type="password"
                      showPassIcon
                    />
                  </FormControl>
                  <FormMessage data-name="createPassword" />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem className="w-full bg-white rounded-md">
                  <FormControl className="w-full focus:outline-none rounded-3xl">
                    <Input
                      {...field}
                      placeholder={t('auth.setPassword.confirmPassword')}
                      type="password"
                      showPassIcon
                    />
                  </FormControl>
                  {passNotMatch && !errors.confirmPassword && (
                    <p className="text-[0.8rem] font-medium text-destructive">
                      {t('auth.setPassword.passwordsDoNotMatch')}
                    </p>
                  )}
                  <FormMessage data-name="confirmPassword" />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="py-3 px-5 font-medium tracking-widest h-11 rounded-3xl"
              isLoading={isLoading}
            >
              {t('auth.setPassword.button')}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  )
}
